import { createContext, useContext, useMemo, memo, ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'

import { FEATURE_FLAG, fetchFeatureFlags } from '@paytrie/common/featureFlags'
import * as Sentry from '@sentry/react'

import { usePageStateStore } from 'src/reducers'

type FeatureFlagsContextValue = {
  enableCadcSolana: boolean
  enableBase: boolean
  enableOtc: boolean
  enable25kLimitIncrease: boolean
  enableSendNewCadc: boolean
  enableReceiveNewCadc: boolean
  enableRejectOldCadc: boolean
  isLoading: boolean
}

const FLAGS = [
  FEATURE_FLAG.ENABLE_CADC_SOLANA,
  FEATURE_FLAG.ENABLE_BASE,
  FEATURE_FLAG.ENABLE_OTC,
  FEATURE_FLAG.ENABLE_25000_LIMIT_INCREASE,
  FEATURE_FLAG.ENABLE_SEND_NEW_CADC,
  FEATURE_FLAG.ENABLE_RECEIVE_NEW_CADC,
  FEATURE_FLAG.ENABLE_REJECT_OLD_CADC,
] as const

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(
  undefined
)

type FeatureFlagsProviderProps = {
  children: ReactNode
}

export const FeatureFlagsProvider = memo(
  ({ children }: FeatureFlagsProviderProps) => {
    const { authToken } = usePageStateStore()

    const { data, isPending } = useQuery({
      queryKey: ['feature-flags', authToken ?? 'anonymous', ...FLAGS],
      queryFn: async () => {
        try {
          return await fetchFeatureFlags(FLAGS)
        } catch (error) {
          console.error(error)
          Sentry.captureException(error)
          return {}
        }
      },
      staleTime: 5 * 60 * 1000,
      retry: 1,
    })

    const enableCadcSolana = data?.[FEATURE_FLAG.ENABLE_CADC_SOLANA] ?? false
    const enableBase = data?.[FEATURE_FLAG.ENABLE_BASE] ?? false
    const enableOtc = data?.[FEATURE_FLAG.ENABLE_OTC] ?? false
    const enable25kLimitIncrease =
      data?.[FEATURE_FLAG.ENABLE_25000_LIMIT_INCREASE] ?? false
    const enableSendNewCadc = data?.[FEATURE_FLAG.ENABLE_SEND_NEW_CADC] ?? false
    const enableReceiveNewCadc =
      data?.[FEATURE_FLAG.ENABLE_RECEIVE_NEW_CADC] ?? false
    const enableRejectOldCadc =
      data?.[FEATURE_FLAG.ENABLE_REJECT_OLD_CADC] ?? false

    const value = useMemo(
      () => ({
        enableCadcSolana,
        enableBase,
        enableOtc,
        enable25kLimitIncrease,
        enableSendNewCadc,
        enableReceiveNewCadc,
        enableRejectOldCadc,
        isLoading: isPending,
      }),
      [
        enableCadcSolana,
        enableBase,
        enableOtc,
        enable25kLimitIncrease,
        enableSendNewCadc,
        enableReceiveNewCadc,
        enableRejectOldCadc,
        isPending,
      ]
    )

    return (
      <FeatureFlagsContext.Provider value={value}>
        {children}
      </FeatureFlagsContext.Provider>
    )
  }
)

FeatureFlagsProvider.displayName = 'FeatureFlagsProvider'

export const useFeatureFlags = (): FeatureFlagsContextValue => {
  const context = useContext(FeatureFlagsContext)
  if (context === undefined) {
    throw new Error(
      'useFeatureFlags must be used within a FeatureFlagsProvider'
    )
  }
  return context
}
